import { getDvaApp } from 'umi';
// type
import type { ConnectState } from '../../../../models/connect.d';
import { ReducersEnum } from '../../../../models/connect.d';
import type {
  HighValueMessageData,
  TestReportSummaryData,
} from '../../utils/typings/aiSummary';
// utils
import { isEmptyObject } from '../../../../utils/common/dataJudgment';
import { compareNum, minusBy } from '../../../../utils/format/number';

const CACHE_KEY_PREFIX = 'ai_summary_cache';
const CACHE_EXPIRY_TIME = 15 * 60 * 1000;

interface CacheData {
  timestamp: number;
  summaryData: TestReportSummaryData | null;
  highValueData: HighValueMessageData | null;
}

/**
 * 生成缓存key
 */
const generateCacheKey = (uuid: string, searchValues: any): string => {
  const searchValuesStr = JSON.stringify(searchValues);
  return `${CACHE_KEY_PREFIX}_${uuid}_${searchValuesStr}`;
};

// 检查缓存是否需要更新 & 获取新的缓存数据
export const getNonExpiredCache = ({
  cacheAiSummaryData,
  currentCache,
}: {
  // 所有缓存数据
  cacheAiSummaryData: Record<string, string>;
  // 指定查询项缓存信息
  currentCache?: CacheData;
}) => {
  const now = Date.now();
  // 记录是否需要更新缓存
  let cacheNeedUpdate = false;
  // 新的缓存数据
  let newCache = {};
  // 传入的指定查询项缓存是否过期
  const isCurrentCacheExpired = currentCache
    ? compareNum(minusBy(now, currentCache.timestamp), CACHE_EXPIRY_TIME) === 1
    : false;

  if (isEmptyObject(cacheAiSummaryData)) {
    return {
      newCache,
      cacheNeedUpdate,
      isCurrentCacheExpired,
    };
  }
  Object.keys(cacheAiSummaryData).forEach((key) => {
    const cache: CacheData = JSON.parse(cacheAiSummaryData[key]);

    // 检查是否未过期
    const isNonExpired =
      compareNum(minusBy(now, cache.timestamp), CACHE_EXPIRY_TIME) !== 1;
    if (!isNonExpired) {
      cacheNeedUpdate = true;
    }
    if (isNonExpired) {
      newCache = {
        ...newCache,
        [key]: cacheAiSummaryData[key],
      };
    }
  });

  return {
    newCache,
    cacheNeedUpdate,
    isCurrentCacheExpired,
  };
};

/**
 * 读取缓存
 */
export const getCache = (uuid: string, searchValues: any): CacheData | null => {
  try {
    const key = generateCacheKey(uuid, searchValues);
    const { dispatch, getState } = getDvaApp()._store;
    const { TesterModel }: ConnectState = getState();
    const cacheStr = TesterModel.cacheAiSummaryData?.[key];
    if (!cacheStr) return null;

    const cache: CacheData = JSON.parse(cacheStr);

    const { newCache, cacheNeedUpdate, isCurrentCacheExpired } =
      getNonExpiredCache({
        cacheAiSummaryData: TesterModel.cacheAiSummaryData,
        currentCache: cache,
      });

    // 修改AI分析报表存储数据
    if (cacheNeedUpdate) {
      dispatch({
        type: ReducersEnum.RESET_TESTER_CACHE_AI_SUMMARY_DATA,
        payload: newCache,
      });
    }
    // 检查当前指定查询项缓存是否过期
    if (isCurrentCacheExpired) {
      return null;
    }

    return cache;
  } catch (error) {
    console.error('读取缓存失败:', error);
    return null;
  }
};

/**
 * 写入缓存
 */
export const setCache = (
  uuid: string,
  searchValues: any,
  summaryData: TestReportSummaryData | null,
  highValueData: HighValueMessageData | null,
): void => {
  const { dispatch } = getDvaApp()._store;
  try {
    const key = generateCacheKey(uuid, searchValues);
    const cacheData: CacheData = {
      timestamp: Date.now(),
      summaryData,
      highValueData,
    };
    // 修改AI分析报表存储数据
    dispatch({
      type: ReducersEnum.SET_TESTER_CACHE_AI_SUMMARY_DATA,
      payload: { [key]: JSON.stringify(cacheData) },
    });
  } catch (error) {
    console.error('写入缓存失败:', error);
  }
};
